import { useEffect, useState, useMemo } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowRight, Briefcase, MapPin, Users, Search } from "lucide-react";
import { useNavigate } from "react-router-dom";

import SEO from "@/components/SEO";
import ApplicationFormModal from "@/components/careers/ApplicationFormModal";
import PublicSearchBar from "@/components/careers/PublicSearchBar";
import { Button } from "@/components/ui/button";
import { useApplicationForm } from "@/hooks/useApplicationForm";
import apiClient from "@/lib/api";
import {
  getContractTypesLabel,
  getOfferContractTypes,
} from "@/lib/jobOffers";

const CareersPage = () => {
  const navigate = useNavigate();
  const [jobOffers, setJobOffers] = useState([]);
  const [totalJobCount, setTotalJobCount] = useState(0);
  const [loadingJobs, setLoadingJobs] = useState(true);
  const [showApplicationForm, setShowApplicationForm] = useState(false);
  const [selectedJob, setSelectedJob] = useState(null);

  // Search state
  const [searchQuery, setSearchQuery] = useState('');

  const {
    applicationData,
    cvFile,
    isSubmitting,
    handleApplicationChange,
    handleFileChange,
    clearCvFile,
    resetApplicationForm,
    submitApplication,
  } = useApplicationForm();

  useEffect(() => {
    const fetchJobs = async () => {
      try {
        setLoadingJobs(true);
        const response = await apiClient.get("/jobs?limit=100&show_all=true");
        setJobOffers(response.data.offers);
        setTotalJobCount(response.data.total_count);
      } catch {
        setJobOffers([]);
        setTotalJobCount(0);
      } finally {
        setLoadingJobs(false);
      }
    };

    fetchJobs();
  }, []);

  const openApplicationForm = (job = null) => {
    setSelectedJob(job);
    setShowApplicationForm(true);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const closeApplicationForm = () => {
    setSelectedJob(null);
    setShowApplicationForm(false);
    resetApplicationForm("cv-upload-page");
  };

  const handleApplicationSubmit = async (event) => {
    event.preventDefault();
    const sent = await submitApplication({ selectedJob });
    if (sent) {
      closeApplicationForm();
    }
  };

  // Filter functionality
  const filteredJobs = useMemo(() => {
    if (!searchQuery.trim()) {
      return jobOffers;
    }

    const lowerQuery = searchQuery.toLowerCase().trim();

    return jobOffers.filter((job) => {
      const matchTitle = String(job.title || "").toLowerCase().includes(lowerQuery);
      const matchLocation = String(job.location || "").toLowerCase().includes(lowerQuery);
      const matchDepartment = String(job.department || "").toLowerCase().includes(lowerQuery);
      const matchContractTypes = getContractTypesLabel(job).toLowerCase().includes(lowerQuery);

      return matchTitle || matchLocation || matchDepartment || matchContractTypes;
    });
  }, [jobOffers, searchQuery]);

  return (
    <>
      <SEO
        title="Lenvia Carrières | Rejoignez nos équipes Mobile & IA"
        description="Découvrez les offres d'emploi Lenvia et postulez aux postes Mobile et IA. Candidature spontanée possible."
      />

      <div className="pt-32 pb-20">
        <div className="lenvia-container">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              className="mb-12"
            >
              <span className="tag mb-4">
                <Users className="w-3 h-3" />
                Nos offres d'emploi
              </span>
              <h1 className="font-heading text-4xl md:text-5xl font-bold text-white mb-4">
                Rejoignez <span className="gradient-text">Lenvia</span>
              </h1>
              <p className="text-lg text-slate-400 max-w-2xl">
                Découvrez toutes nos opportunités et trouvez le poste qui correspond à vos ambitions.
              </p>

              <div className="mt-8 mb-4">
                <PublicSearchBar
                  onSearch={setSearchQuery}
                />
              </div>

              <div className="flex justify-center items-center mt-6">
                <p className="text-sm text-slate-400 font-medium">
                  {searchQuery ? (
                    filteredJobs.length === 0 ? "Aucun poste trouvé" : `${filteredJobs.length} poste${filteredJobs.length > 1 ? 's' : ''} trouvé${filteredJobs.length > 1 ? 's' : ''}`
                  ) : (
                    totalJobCount > 0 ? `${totalJobCount} postes ouverts` : ""
                  )}
                </p>
              </div>
            </motion.div>

            <ApplicationFormModal
              open={showApplicationForm}
              onClose={closeApplicationForm}
              selectedJob={selectedJob}
              applicationData={applicationData}
              cvFile={cvFile}
              isSubmitting={isSubmitting}
              onApplicationChange={handleApplicationChange}
              onFileChange={handleFileChange}
              onClearFile={clearCvFile}
              onSubmit={handleApplicationSubmit}
              fileInputId="cv-upload-page"
            />

            {loadingJobs ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {[1, 2, 3, 4, 5, 6].map((i) => (
                  <div key={i} className="glass-card p-6 animate-pulse">
                    <div className="h-5 bg-white/10 rounded w-3/4 mb-3"></div>
                    <div className="h-4 bg-white/5 rounded w-1/2 mb-3"></div>
                    <div className="h-3 bg-white/5 rounded w-1/3"></div>
                  </div>
                ))}
              </div>
            ) : jobOffers.length > 0 ? (
              filteredJobs.length > 0 ? (
                <motion.div layout className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  <AnimatePresence mode="popLayout">
                    {filteredJobs.map((job) => {
                      const contractTypes = getOfferContractTypes(job);

                      return (
                        <motion.div
                          key={job.id}
                          layout
                          initial={{ opacity: 0, scale: 0.9, y: 20 }}
                          animate={{ opacity: 1, scale: 1, y: 0 }}
                          exit={{ opacity: 0, scale: 0.9, y: -20 }}
                          transition={{ duration: 0.3 }}
                          whileHover={{ y: -5 }}
                          className="glass-card p-6 cursor-pointer group flex flex-col h-full"
                          onClick={() => navigate(`/offre/${job.id}`)}
                          data-testid={`job-${job.id}`}
                        >
                          <div className="flex-grow">
                            <div className="flex items-start justify-between mb-3">
                              <h4 className="font-heading font-semibold text-white group-hover:text-lenvia-blue transition-colors pr-2">
                                {job.title}
                              </h4>
                              <ArrowRight className="w-4 h-4 text-slate-500 group-hover:text-lenvia-blue transition-colors flex-shrink-0 mt-1" />
                            </div>
                            <div className="flex flex-wrap items-center gap-3 mb-3 text-sm text-slate-400">
                              {contractTypes.length > 0 && (
                                <div className="flex flex-wrap items-center gap-2">
                                  <Briefcase className="w-4 h-4" />
                                  {contractTypes.map((contractType) => (
                                    <span
                                      key={`${job.id}-${contractType}`}
                                      className="rounded-full border border-white/10 bg-white/5 px-2 py-1 text-xs text-slate-300"
                                    >
                                      {contractType}
                                    </span>
                                  ))}
                                </div>
                              )}
                              {job.location && (
                                <span className="flex items-center gap-1">
                                  <MapPin className="w-4 h-4" />
                                  {job.location}
                                </span>
                              )}
                            </div>
                            {job.department && (
                              <span className="inline-block text-xs px-2.5 py-1 bg-lenvia-violet/20 rounded-full text-lenvia-violet border border-lenvia-violet/30 mb-3">
                                {job.department}
                              </span>
                            )}
                            {job.salary_range && <p className="text-sm text-slate-500 mb-4">{job.salary_range}</p>}
                          </div>
                          <button
                            type="button"
                            className="w-full mt-4 py-2.5 px-4 bg-white/5 border border-white/10 rounded-lg text-sm text-slate-300 group-hover:bg-lenvia-blue/20 group-hover:border-lenvia-blue/30 group-hover:text-lenvia-blue transition-all flex items-center justify-center gap-2"
                          >
                            Postuler
                            <ArrowRight className="w-4 h-4" />
                          </button>
                        </motion.div>
                      );
                    })}
                  </AnimatePresence>
                </motion.div>
              ) : (
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  className="glass-card p-12 text-center"
                >
                  <Search className="w-16 h-16 text-slate-500 mx-auto mb-4 opacity-50" />
                  <h3 className="font-heading text-xl font-semibold text-white mb-2">Aucun poste trouvé</h3>
                  <p className="text-slate-400 mb-6">Nous n'avons pas trouvé de poste correspondant à votre recherche "{searchQuery}".</p>
                  <Button className="btn-primary" onClick={() => setSearchQuery('')}>
                    Voir toutes les offres ({jobOffers.length})
                    <ArrowRight className="w-4 h-4" />
                  </Button>
                </motion.div>
              )
            ) : (
              <div className="glass-card p-12 text-center">
                <Briefcase className="w-16 h-16 text-slate-500 mx-auto mb-4" />
                <h3 className="font-heading text-xl font-semibold text-white mb-2">Aucune offre disponible</h3>
                <p className="text-slate-400 mb-6">Pas d'inquiétude, envoyez-nous une candidature spontanée !</p>
                <Button className="btn-primary" onClick={() => openApplicationForm(null)}>
                  Candidature spontanée
                  <ArrowRight className="w-4 h-4" />
                </Button>
              </div>
            )}

            {jobOffers.length > 0 && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.3 }}
                className="mt-12 glass-card p-8 text-center"
              >
                <h3 className="font-heading text-xl font-semibold text-white mb-2">Vous ne trouvez pas le poste idéal ?</h3>
                <p className="text-slate-400 mb-6">
                  Envoyez-nous une candidature spontanée et rejoignez notre vivier de talents.
                </p>
                <Button className="btn-primary" onClick={() => openApplicationForm(null)}>
                  Candidature spontanée
                  <ArrowRight className="w-4 h-4" />
                </Button>
              </motion.div>
            )}
        </div>
      </div>
    </>
  );
};

export default CareersPage;
